import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function Parties({ kind }) {
  const label = kind === "customer" ? "Customer" : "Supplier";
  const [parties, setParties] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());

  function empty() {
    return {
      name: "",
      kind,
      gstin: "",
      email: "",
      phone: "",
      address: "",
      state: "",
      credit_days: 30,
      opening_balance: 0,
    };
  }

  const load = async () => {
    const { data } = await api.get("/parties", { params: { kind } });
    setParties(data);
  };
  useEffect(() => {
    load();
  }, [kind]);

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/parties/${editing.party_id}`, { ...form, kind });
        toast.success(`${label} updated`);
      } else {
        await api.post("/parties", { ...form, kind });
        toast.success(`${label} added`);
      }
      setOpen(false);
      setEditing(null);
      setForm(empty());
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm(`Delete this ${label.toLowerCase()}?`)) return;
    await api.delete(`/parties/${id}`);
    toast.success("Deleted");
    load();
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...empty(), ...p });
    setOpen(true);
  };

  return (
    <div>
      <PageHeader
        title={`${label}s`}
        subtitle={`Manage all your ${label.toLowerCase()}s`}
        action={
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(empty()); } }}>
            <DialogTrigger asChild>
              <Button
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid={`add-${kind}-btn`}
              >
                <Plus className="w-4 h-4 mr-1" /> Add {label}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editing ? "Edit" : "New"} {label}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
                <Field label="Name">
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="party-name" />
                </Field>
                <Field label="GSTIN">
                  <Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} />
                </Field>
                <Field label="Email">
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </Field>
                <Field label="Phone">
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </Field>
                <Field label="State">
                  <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="e.g. Maharashtra" />
                </Field>
                <Field label="Credit Days">
                  <Input type="number" value={form.credit_days} onChange={(e) => setForm({ ...form, credit_days: +e.target.value })} />
                </Field>
                <Field label="Opening Balance (₹)">
                  <Input type="number" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: +e.target.value })} />
                </Field>
                <div className="md:col-span-2">
                  <Field label="Address">
                    <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                  </Field>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="save-party-btn">
                  {editing ? "Save changes" : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="px-8 py-6">
        <Card className="border-slate-200 overflow-hidden">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>GSTIN</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>State</TableHead>
                <TableHead className="text-right">Credit Days</TableHead>
                <TableHead className="text-right">Opening Bal</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {parties.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-slate-500">
                    No {label.toLowerCase()}s yet. Add your first one.
                  </TableCell>
                </TableRow>
              )}
              {parties.map((p) => (
                <TableRow key={p.party_id} data-testid={`party-row-${p.party_id}`}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="text-slate-500">{p.gstin || "—"}</TableCell>
                  <TableCell className="text-slate-500">
                    {p.email || "—"}<br />
                    <span className="text-xs">{p.phone}</span>
                  </TableCell>
                  <TableCell className="text-slate-500">{p.state || "—"}</TableCell>
                  <TableCell className="text-right number-cell">{p.credit_days}</TableCell>
                  <TableCell className="text-right number-cell">{Number(p.opening_balance || 0).toFixed(2)}</TableCell>
                  <TableCell className="text-right space-x-2">
                    <Link to={`/ledger?party_id=${p.party_id}`}>
                      <Button variant="ghost" size="icon" title="Ledger">
                        <BookOpen className="w-4 h-4" />
                      </Button>
                    </Link>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(p)} title="Edit">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => del(p.party_id)} title="Delete">
                      <Trash2 className="w-4 h-4 text-rose-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-slate-600">{label}</Label>
      {children}
    </div>
  );
}
